<script setup>
import { computed } from 'vue';

const siteUrl = 'https://gefestai.com'; // Замени на свой URL или используй useRuntimeConfig()

// 1. SSR Запрос категорий из Laravel
const { data: apiCategories, pending, error } = await useFetch(`${siteUrl}/api/categories`, {
    key: 'blog-categories',
    // Если Laravel оборачивает ответ в "data", достаем его
    transform: (res) => res.data || res
});

// 2. Пресеты стилей для карточек (так как в БД этого нет)
// Используем массив, чтобы циклично применять разные цвета и размеры
const designPresets = [
    { size: 'lg:col-span-2 lg:row-span-2', gradient: 'from-purple-600/20 to-indigo-600/0', glow: 'group-hover:shadow-[0_0_40px_-10px_rgba(147,51,234,0.3)]' },
    { size: 'col-span-1', gradient: 'from-orange-600/20 to-red-600/0', glow: 'group-hover:shadow-[0_0_40px_-10px_rgba(234,88,12,0.3)]' },
    { size: 'col-span-1', gradient: 'from-emerald-600/20 to-teal-600/0', glow: 'group-hover:shadow-[0_0_40px_-10px_rgba(16,185,129,0.3)]' },
    { size: 'lg:col-span-2', gradient: 'from-blue-600/20 to-cyan-600/0', glow: 'group-hover:shadow-[0_0_40px_-10px_rgba(37,99,235,0.3)]' },
];

// 3. Склеиваем данные API с визуальными пресетами
const categories = computed(() => {
    if (!apiCategories.value) return [];

    return apiCategories.value.map((cat, index) => {
        const preset = designPresets[index % designPresets.length];

        // Получаем путь к картинке из первой статьи
        // Если картинки нет, можно оставить заглушку или градиент
        const firstArticleImage = cat.articles?.[0]?.cover_image
            ? `${siteUrl}/storage/${cat.articles[0].cover_image}`
            : null;
        return {
            ...cat,
            title: cat.name,
            articleCount: cat.articles_count || 0,
            coverImage: firstArticleImage, // Сохраняем путь
            size: preset.size,
            gradient: preset.gradient,
            hoverGlow: preset.glow,
            trending: cat.articles?.length > 0
                ? cat.articles.map(a => a.title)
                : ['Статьи скоро появятся']
        };
    });
});

const activeFilter = ref('Все');
const filters = ['Все', 'Популярное', 'Для новичков', 'Хардкор'];
</script>

<template>
  <div class="min-h-screen bg-[#0F1113] text-gray-300 relative flex flex-col font-sans selection:bg-orange-500/30">

    <div class="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none fixed">
      <div class="absolute -top-[10%] left-[10%] w-[40%] h-[40%] bg-orange-900/10 rounded-full blur-[120px] animate-pulse-slow"></div>
      <div class="absolute top-[30%] right-[10%] w-[30%] h-[30%] bg-blue-900/10 rounded-full blur-[150px] animate-pulse-slow" style="animation-delay: 2s"></div>
    </div>

    <nav class="relative z-20 px-6 py-6 max-w-7xl mx-auto w-full flex justify-between items-center">
      <NuxtLink to="/" class="flex items-center gap-2 text-gray-400 hover:text-white transition group">
        <div class="w-8 h-8 rounded-lg bg-[#1A1C1E] border border-gray-700 group-hover:border-orange-500 flex items-center justify-center transition-colors">
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
        </div>
        <span class="text-sm font-medium">Назад на Gefest AI</span>
      </NuxtLink>
    </nav>

    <main class="relative z-10 flex-grow px-4 sm:px-6 lg:px-8 pb-20">
      <div class="max-w-7xl mx-auto">

        <div class="mt-10 mb-16 animate-fade-in-up">
          <div class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400 text-xs font-bold uppercase tracking-widest mb-6">
            <span class="w-2 h-2 rounded-full bg-orange-500 animate-pulse"></span>
            База знаний
          </div>
          <h1 class="text-5xl md:text-7xl font-extrabold tracking-tight mb-6">
            Погрузитесь в <br />
            <span class="text-transparent bg-clip-text bg-gradient-to-r from-white via-gray-300 to-gray-600">
              мир технологий.
            </span>
          </h1>
          <p class="text-lg text-gray-400 max-w-2xl leading-relaxed">
            Инсайты, гайды и реальные кейсы. Узнайте, как строить системы без кода, оптимизировать бизнес с помощью ИИ и ловить флоу в вайбкодинге.
          </p>
        </div>

        <div class="flex flex-wrap gap-3 mb-12 animate-fade-in-up" style="animation-delay: 0.1s;">
          <button
              v-for="filter in filters" :key="filter"
              @click="activeFilter = filter"
              :class="[
              'px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 border backdrop-blur-md',
              activeFilter === filter
                ? 'bg-orange-500 text-white border-orange-400 shadow-[0_0_20px_-5px_rgba(234,88,12,0.4)]'
                : 'bg-[#131416]/80 text-gray-400 border-white/5 hover:border-gray-600 hover:text-white'
            ]"
          >
            {{ filter }}
          </button>
        </div>

          <div class="max-w-7xl mx-auto">
              <div v-if="pending" class="flex justify-center items-center py-20 w-full">
                  <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
              </div>

              <div v-else-if="error" class="text-center py-20 text-red-400 w-full">
                  Ошибка загрузки нейронных связей...
              </div>

              <div v-else
                   class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-[280px] grid-flow-row-dense animate-fade-in-up"
                   style="animation-delay: 0.2s;"
              >
                  <NuxtLink
                      v-for="cat in categories"
                      :key="cat.id"
                      :to="`/blog/${cat.slug || cat.id}`"
                      class="group relative rounded-3xl overflow-hidden bg-[#131416]/80 backdrop-blur-md border border-white/5 transition-all duration-500 flex flex-col w-full h-full"
                      :class="[
        cat.size ? cat.size : 'col-span-1',
        cat.hoverGlow ? cat.hoverGlow : 'group-hover:shadow-lg'
      ]"
                  >
                      <div class="absolute inset-0 z-0">
                          <img
                              v-if="cat.coverImage"
                              :src="cat.coverImage"
                              class="w-full h-full object-cover transition-transform duration-700 scale-105 group-hover:scale-110 opacity-40 group-hover:opacity-60"
                              alt=""
                          />

                          <div
                              class="absolute inset-0 bg-gradient-to-b from-[#0F1113]/60 via-transparent to-[#0F1113]/90 group-hover:from-[#0F1113]/40 transition-colors duration-500"
                          ></div>

                          <div
                              class="absolute inset-0 opacity-20 group-hover:opacity-40 transition-opacity duration-700 bg-gradient-to-br"
                              :class="cat.gradient"
                          ></div>
                      </div>

                    <div class="absolute inset-0 opacity-[0.03] bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-white via-transparent to-transparent pointer-events-none z-0"></div>

                    <div class="relative z-10 p-8 flex flex-col h-full w-full justify-between">

                        <div class="flex justify-between items-start w-full">
                            <h3 class="text-2xl lg:text-3xl font-bold text-white group-hover:text-orange-100 transition-colors pr-4">
                                {{ cat.title }}
                            </h3>
                            <span class="flex-shrink-0 flex items-center justify-center min-w-[40px] h-10 px-2 rounded-full bg-white/5 border border-white/10 text-sm font-medium text-gray-300 group-hover:bg-white/10 transition-colors">
        {{ cat.articleCount }}
      </span>
                        </div>

                        <div class="mt-8 relative flex-grow overflow-hidden min-h-[80px]">
                            <p class="text-gray-400 leading-relaxed transition-all duration-500 transform group-hover:-translate-y-full group-hover:opacity-0">
                                {{ cat.description }}
                            </p>

                            <div class="absolute inset-0 transition-all duration-500 transform translate-y-full opacity-0 group-hover:translate-y-0 group-hover:opacity-100 flex flex-col gap-2">
                                <span class="text-xs font-bold text-orange-500 uppercase tracking-wider mb-1">Популярное в 2026:</span>
                                <div v-if="cat.trending && cat.trending.length">
                                    <p v-for="(article, idx) in cat.trending.slice(0, 2)" :key="idx" class="text-sm text-gray-200 truncate flex items-center gap-2 mb-1">
                                        <span class="w-1.5 h-1.5 rounded-full bg-orange-500 shadow-[0_0_8px_rgba(234,88,12,0.6)]"></span>
                                        {{ article }}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div class="mt-6 flex items-center text-sm font-bold text-orange-500 opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-500 delay-100">
                            <span class="uppercase tracking-widest text-[11px]">Смотреть базу знаний</span>
                            <svg class="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                        </div>
                    </div>
                </NuxtLink>
            </div>
        </div>
      </div>
    </main>
  </div>
</template>

<style scoped>
.animate-pulse-slow {
  animation: pulse 10s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
}

.animate-fade-in-up {
  animation: fadeInUp 0.8s ease-out forwards;
  opacity: 0;
}
</style>
